/**
 * BudgetLimitProgressCard Component
 * Displays a budget limit (plafond) with spent/limit progress bar.
 * Supports both category-based and tag-based limits.
 */

import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import { formatCurrency } from '../../utils/formatters';
import type { BudgetLimit } from '../../api/types';

interface BudgetLimitProgressCardProps {
  budgetLimit: BudgetLimit;
  onPress?: () => void;
}

const CATEGORY_ICONS: Record<string, string> = {
  housing: 'home',
  food: 'food',
  transport: 'car',
  insurance: 'shield-check',
  subscriptions: 'cellphone',
  leisure: 'gamepad-variant',
  health: 'heart-pulse',
  taxes: 'file-document',
  education: 'school',
  family_assistance: 'account-group',
  children_expenses: 'baby-carriage',
  savings: 'piggy-bank',
  shopping: 'shopping',
  other: 'dots-horizontal',
};

const CATEGORY_COLORS: Record<string, string> = {
  housing: '#8B4513',
  food: '#FF9500',
  transport: '#007AFF',
  insurance: '#5AC8FA',
  subscriptions: '#AF52DE',
  leisure: '#FF2D55',
  health: '#FF3B30',
  taxes: '#8E8E93',
  education: '#3B82F6',
  family_assistance: '#34C759',
  children_expenses: '#FF6B6B',
  savings: '#34C759',
  shopping: '#FF9500',
  other: '#8E8E93',
};

function getIconForLimit(bl: BudgetLimit): string {
  if (bl.icon) return bl.icon;
  if (bl.tag) return 'tag';
  return CATEGORY_ICONS[bl.category || ''] || 'tag';
}

function getColorForLimit(bl: BudgetLimit): string {
  if (bl.color) return bl.color;
  if (bl.tag) return '#7B68EE';
  return CATEGORY_COLORS[bl.category || ''] || colors.primary;
}

export default function BudgetLimitProgressCard({
  budgetLimit,
  onPress,
}: BudgetLimitProgressCardProps) {
  const spent = budgetLimit.spent_amount || 0;
  const limit = budgetLimit.monthly_limit;
  const percentage = limit > 0 ? Math.min((spent / limit) * 100, 100) : 0;
  const remaining = limit - spent;

  const iconName = getIconForLimit(budgetLimit);
  const iconColor = getColorForLimit(budgetLimit);

  const isTagBased = !!budgetLimit.tag;
  const subtitle = isTagBased ? `Tag : ${budgetLimit.tag}` : null;

  const getProgressColor = (): string => {
    if (percentage >= 90) return colors.error;
    if (percentage >= 70) return colors.warning;
    return colors.success;
  };

  const progressColor = getProgressColor();

  return (
    <TouchableOpacity
      style={styles.card}
      onPress={onPress}
      activeOpacity={onPress ? 0.7 : 1}
    >
      <View style={styles.header}>
        <View style={[styles.iconContainer, { backgroundColor: iconColor + '20' }]}>
          <Icon name={iconName} size={20} color={iconColor} />
        </View>
        <View style={styles.headerText}>
          <Text style={styles.limitName}>{budgetLimit.name}</Text>
          {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
          <Text style={styles.amountText}>
            {formatCurrency(spent)} / {formatCurrency(limit)}
          </Text>
        </View>
        {onPress && (
          <Icon name="pencil-outline" size={18} color={colors.textSecondary} />
        )}
      </View>

      {/* Progress bar */}
      <View style={styles.progressBarContainer}>
        <View
          style={[
            styles.progressBarFill,
            {
              width: `${percentage}%`,
              backgroundColor: progressColor,
            },
          ]}
        />
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <Text style={[styles.percentageText, { color: progressColor }]}>
          {Math.round(percentage)}% utilisé
        </Text>
        <Text
          style={[
            styles.remainingText,
            remaining < 0 && styles.remainingTextOver,
          ]}
        >
          {remaining >= 0
            ? `${formatCurrency(remaining)} restant`
            : `${formatCurrency(Math.abs(remaining))} dépassé`}
        </Text>
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.background,
    borderRadius: 12,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  iconContainer: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  headerText: {
    flex: 1,
  },
  limitName: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
  },
  subtitle: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginTop: 2,
  },
  amountText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginTop: 2,
  },
  progressBarContainer: {
    height: 8,
    backgroundColor: colors.border,
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: spacing.xs,
  },
  progressBarFill: {
    height: '100%',
    borderRadius: 4,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  percentageText: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold as '600',
  },
  remainingText: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
  },
  remainingTextOver: {
    color: colors.error,
    fontWeight: typography.fontWeight.semibold as '600',
  },
});
